// src/components/TokenTransfer.js
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Send, 
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Copy,
  ExternalLink,
  Coins
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import './TokenTransfer.css';

// WorldLand Explorer API 클래스 (TokenAirdrop.js와 동일)
class WorldLandExplorerAPI {
  constructor() {
    this.baseUrl = 'https://scan.worldland.foundation';
  }

  // 계정의 모든 토큰 잔액 조회
  async getAccountTokenBalances(address) {
    try {
      const endpoint = `${this.baseUrl}/api/v2/addresses/${address}/token-balances`;
      
      console.log('토큰 잔액 조회 API 요청:', endpoint);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(15000)
      });

      if (!response.ok) {
        throw new Error(`토큰 잔액 API 요청 실패: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // 잔액이 0이 아닌 토큰만 필터링
      const nonZeroTokens = (data || []).filter(token => {
        const balance = token.value || token.balance || '0';
        return balance !== '0' && parseFloat(balance) > 0;
      });
      
      return {
        success: true,
        tokens: this.formatTokenBalances(nonZeroTokens),
        source: 'blockscout-balances'
      };

    } catch (error) {
      console.warn('토큰 잔액 조회 실패:', error.message);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 토큰 잔액 데이터 포맷팅
  formatTokenBalances(tokens) {
    return tokens.map(token => ({
      address: token.token?.address || token.address,
      name: token.token?.name || token.name || 'Unknown Token',
      symbol: token.token?.symbol || token.symbol || 'UNK',
      decimals: parseInt(token.token?.decimals || token.decimals || '18'),
      balance: this.formatTokenAmount(
        token.value || token.balance || '0', 
        parseInt(token.token?.decimals || token.decimals || '18')
      ),
      balanceRaw: token.value || token.balance || '0',
      type: token.token?.type || 'ERC-20',
      verified: token.token?.is_verified || false
    }));
  }

  // 토큰 수량 포맷팅
  formatTokenAmount(amount, decimals) {
    try {
      if (!amount || amount === '0') return '0';
      const divisor = Math.pow(10, decimals);
      const formatted = (parseInt(amount) / divisor).toString();
      return parseFloat(formatted).toFixed(6);
    } catch {
      return '0';
    }
  }

  // WLC 잔액 조회
  async getWLCBalance(address) {
    try {
      const provider = window.selectedEthereumProvider || window.ethereum;
      const balance = await provider.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      const balanceInEther = parseInt(balance, 16) / Math.pow(10, 18);
      return balanceInEther;
    } catch (error) {
      console.error('WLC 잔액 조회 실패:', error);
      return 0;
    }
  }

  // 특정 토큰 잔액 조회
  async getTokenBalance(address, tokenAddress, decimals = 18) {
    try {
      const provider = window.selectedEthereumProvider || window.ethereum;
      
      // ERC-20 balanceOf function signature
      const balanceOfFunction = '0x70a08231';
      const paddedAddress = address.substring(2).padStart(64, '0');
      const data = balanceOfFunction + paddedAddress;

      const result = await provider.request({
        method: 'eth_call',
        params: [{
          to: tokenAddress,
          data: data
        }, 'latest']
      });

      const balance = parseInt(result, 16) / Math.pow(10, decimals);
      return balance;
    } catch (error) {
      console.error('토큰 잔액 조회 실패:', error);
      return 0;
    }
  }

  // 기본 토큰 목록 (API 실패 시 사용)
  getDefaultTokenList() {
    const defaultTokens = [
      {
        address: '0xc93f06d582fd6fD7FfEd928ffB57896474abC855',
        name: 'SAPSALDOG',
        symbol: 'Dokdo',
        decimals: 18,
        verified: true
      },
      {
        address: '0xDBEDbeB2CD8493AE2d746b7eeD597946F6E7910A',
        name: 'WorldLand Cafe',
        symbol: 'CAFE',
        decimals: 18,
        verified: true
      },
      {
        address: '0x6f57a06f1eA3c6EB16F742249CBdCB672458337C',
        name: 'WorldLand-Pay',
        symbol: 'WLP',
        decimals: 18,
        verified: true
      },
      {
        address: '0xC0AD918E5CC5930B8D31cc229770f232Dd82187a',
        name: 'Doldari',
        symbol: 'DOL',
        decimals: 18,
        verified: true
      },
      {
        address: '0x12183c232B94a6FB80711037b8A407a03Dd24983',
        name: 'World Vision',
        symbol: 'VISION',
        decimals: 18,
        verified: true
      }
    ];

    return {
      success: true,
      tokens: defaultTokens.map(token => ({
        ...token,
        balance: '0',
        balanceRaw: '0'
      })),
      source: 'default-tokens'
    };
  }
}

const TokenTransfer = ({ account, onBack, user, userProfile }) => {
  // 상태 관리
  const [userTokens, setUserTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successTx, setSuccessTx] = useState(null);
  const [explorerAPI] = useState(new WorldLandExplorerAPI());
  const [gasEstimate, setGasEstimate] = useState(null);
  const [customTokenAddress, setCustomTokenAddress] = useState('');
  const [transferStatus, setTransferStatus] = useState('idle'); // 'idle', 'preparing', 'pending', 'confirming', 'completed', 'failed'
  const [transferProgress, setTransferProgress] = useState('');
  const [transferResult, setTransferResult] = useState(null);
  const [actualGasUsed, setActualGasUsed] = useState(null);

  // 초기 데이터 로드
  useEffect(() => {
    if (account) {
      fetchUserTokens();
    }
  }, [account]);

  // 사용자 토큰 목록 가져오기
  const fetchUserTokens = async () => {
    setIsLoadingTokens(true);
    console.log(`🔍 ${account} 토큰 목록 조회 시작...`);
    
    try {
      const tokens = [];

      // 1단계: WLC 잔액 조회
      console.log('1️⃣ WLC 잔액 조회...');
      const wlcBalance = await explorerAPI.getWLCBalance(account);
      tokens.push({
        name: 'WorldLand Coin',
        symbol: 'WLC',
        address: 'native',
        balance: wlcBalance.toFixed(6),
        decimals: 18,
        isNative: true,
        verified: true
      });
      console.log(`✅ WLC 잔액: ${wlcBalance.toFixed(6)}`);

      // 2단계: Explorer API를 통해 ERC-20 토큰 조회
      console.log('2️⃣ Explorer API로 ERC-20 토큰 조회...');
      try {
        const tokenBalanceResult = await explorerAPI.getAccountTokenBalances(account);
        if (tokenBalanceResult.success && tokenBalanceResult.tokens.length > 0) {
          tokens.push(...tokenBalanceResult.tokens.map(token => ({
            ...token,
            isNative: false
          })));
          console.log(`✅ Explorer API에서 ${tokenBalanceResult.tokens.length}개 토큰 발견`);
        } else {
          console.log('⚪ Explorer API에서 토큰 없음');
        }
      } catch (apiError) {
        console.warn('⚠️ Explorer API 실패:', apiError);
      }

      // 3단계: 잔액이 0인 토큰 제거 (WLC는 제외)
      const nonZeroTokens = tokens.filter(token => 
        token.isNative || parseFloat(token.balance) > 0
      );

      console.log(`🎯 최종 토큰 목록: ${nonZeroTokens.length}개`);
      setUserTokens(nonZeroTokens);

    } catch (error) {
      console.error('❌ 토큰 목록 조회 실패:', error);
      // 실패 시 최소한 WLC는 보여주기
      const wlcBalance = await explorerAPI.getWLCBalance(account);
      setUserTokens([{
        name: 'WorldLand Coin',
        symbol: 'WLC',
        address: 'native',
        balance: wlcBalance.toFixed(6),
        decimals: 18,
        isNative: true,
        verified: true
      }]);
    } finally {
      setIsLoadingTokens(false);
    }
  };

  // 토큰 선택
  const selectToken = (token) => {
    setSelectedToken(token);
    setCustomTokenAddress('');
    setErrors([]);
    setGasEstimate(null);
  };

  // 커스텀 토큰 주소 설정
  const handleCustomTokenAddress = (address) => {
    setCustomTokenAddress(address);
    setSelectedToken(null);
    setErrors([]);
    setGasEstimate(null);
  };

  // 주소 유효성 검사
  const isValidAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // 수량 유효성 검사
  const isValidAmount = (amount) => {
    const num = parseFloat(amount);
    return !isNaN(num) && num > 0;
  };

  // 가스 예상 수수료 계산
  const estimateGas = async () => {
    if (!recipientAddress || !transferAmount || (!selectedToken && !customTokenAddress)) {
      return;
    }

    try {
      const provider = window.selectedEthereumProvider || window.ethereum;
      let gasEstimate;

      if (selectedToken?.isNative || !selectedToken) {
        // WLC 전송
        const amountInWei = (parseFloat(transferAmount) * Math.pow(10, 18)).toString(16);
        gasEstimate = await provider.request({
          method: 'eth_estimateGas',
          params: [{
            from: account,
            to: recipientAddress,
            value: '0x' + amountInWei,
          }]
        });
      } else {
        // ERC-20 토큰 전송
        const tokenAddr = selectedToken ? selectedToken.address : customTokenAddress;
        const amountInWei = (parseFloat(transferAmount) * Math.pow(10, 18)).toString(16);
        
        const transferFunction = '0xa9059cbb';
        const paddedAddress = recipientAddress.substring(2).padStart(64, '0');
        const paddedAmount = amountInWei.padStart(64, '0');
        const data = transferFunction + paddedAddress + paddedAmount;

        gasEstimate = await provider.request({
          method: 'eth_estimateGas',
          params: [{
            from: account,
            to: tokenAddr,
            data: data,
          }]
        });
      }

      const gasInWei = parseInt(gasEstimate, 16);
      const gasInEther = gasInWei / Math.pow(10, 18);
      setGasEstimate(gasInEther.toFixed(6));

    } catch (error) {
      console.error('가스 예상 실패:', error);
      setGasEstimate('계산 실패');
    }
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const newErrors = [];

    if (!recipientAddress) {
      newErrors.push('수신자 주소를 입력해주세요.');
    } else if (!isValidAddress(recipientAddress)) {
      newErrors.push('유효한 수신자 주소를 입력해주세요.');
    }

    if (!transferAmount) {
      newErrors.push('전송할 수량을 입력해주세요.');
    } else if (!isValidAmount(transferAmount)) {
      newErrors.push('유효한 수량을 입력해주세요.');
    }

    if (!selectedToken && !customTokenAddress) {
      newErrors.push('전송할 토큰을 선택하거나 토큰 주소를 입력해주세요.');
    }

    if (customTokenAddress && !isValidAddress(customTokenAddress)) {
      newErrors.push('유효한 토큰 주소를 입력해주세요.');
    }

    // 잔액 확인
    if (selectedToken && parseFloat(transferAmount) > parseFloat(selectedToken.balance)) {
      newErrors.push(`보유 잔액(${selectedToken.balance} ${selectedToken.symbol})이 부족합니다.`);
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // WLC 전송
  const sendWLC = async () => {
    const amountInWei = (parseFloat(transferAmount) * Math.pow(10, 18)).toString(16);
    
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    const txParams = {
      from: account,
      to: recipientAddress,
      value: '0x' + amountInWei,
      gas: '0x5208',
    };

    return await provider.request({
      method: 'eth_sendTransaction',
      params: [txParams],
    });
  };

  // ERC-20 토큰 전송
  const sendToken = async () => {
    const amountInWei = (parseFloat(transferAmount) * Math.pow(10, 18)).toString(16);
    
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    const transferFunction = '0xa9059cbb';
    const paddedAddress = recipientAddress.substring(2).padStart(64, '0');
    const paddedAmount = amountInWei.padStart(64, '0');
    const data = transferFunction + paddedAddress + paddedAmount;

    const tokenAddr = selectedToken ? selectedToken.address : customTokenAddress;

    const txParams = {
      from: account,
      to: tokenAddr,
      data: data,
      gas: '0x186a0',
    };

    return await provider.request({
      method: 'eth_sendTransaction',
      params: [txParams],
    });
  };

  // 거래 상태 확인 및 영수증 조회
  const waitForTransaction = async (txHash) => {
    const provider = window.selectedEthereumProvider || window.ethereum;
    let receipt = null;
    let attempts = 0;
    const maxAttempts = 60; // 최대 60초 대기

    setTransferProgress('거래 확인 중...');
    
    while (!receipt && attempts < maxAttempts) {
      try {
        receipt = await provider.request({
          method: 'eth_getTransactionReceipt',
          params: [txHash]
        });
        
        if (!receipt) {
          attempts++;
          setTransferProgress(`거래 확인 중... (${attempts}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.warn('거래 영수증 조회 실패:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    if (receipt) {
      // 가스 사용량 계산
      const gasUsed = parseInt(receipt.gasUsed, 16);
      const gasPrice = parseInt(receipt.effectiveGasPrice || '0x0', 16);
      const gasFee = (gasUsed * gasPrice) / Math.pow(10, 18);
      setActualGasUsed(gasFee.toFixed(6));

      // 거래 성공/실패 확인
      const success = receipt.status === '0x1';
      return { success, receipt, gasUsed: gasFee };
    }
    
    return { success: false, receipt: null, gasUsed: null };
  };

  // 잔액 즉시 업데이트
  const updateTokenBalance = async (token) => {
    try {
      if (token.isNative) {
        const newBalance = await explorerAPI.getWLCBalance(account);
        setUserTokens(prev => prev.map(t => 
          t.address === token.address 
            ? { ...t, balance: newBalance.toFixed(6) }
            : t
        ));
        
        if (selectedToken?.address === token.address) {
          setSelectedToken(prev => ({ ...prev, balance: newBalance.toFixed(6) }));
        }
      } else {
        const newBalance = await explorerAPI.getTokenBalance(
          account, 
          token.address, 
          token.decimals
        );
        
        const formattedBalance = newBalance.toFixed(6);
        setUserTokens(prev => prev.map(t => 
          t.address === token.address 
            ? { ...t, balance: formattedBalance }
            : t
        ));
        
        if (selectedToken?.address === token.address) {
          setSelectedToken(prev => ({ ...prev, balance: formattedBalance }));
        }
      }
    } catch (error) {
      console.error('잔액 업데이트 실패:', error);
    }
  };

  // 전송 실행
  const executeTransfer = async () => {
    if (!validateForm()) return;

    const tokenToTransfer = selectedToken || {
      address: customTokenAddress,
      symbol: 'TOKEN',
      isNative: false,
      decimals: 18
    };

    const confirmMessage = `다음 내용으로 전송하시겠습니까?\n\n` +
      `토큰: ${tokenToTransfer.symbol}\n` +
      `수량: ${transferAmount}\n` +
      `수신자: ${recipientAddress}\n` +
      `예상 가스비: ${gasEstimate || '계산 중...'} WLC`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // 초기화
    setIsProcessing(true);
    setErrors([]);
    setSuccessTx(null);
    setTransferResult(null);
    setActualGasUsed(null);
    setTransferStatus('preparing');
    setTransferProgress('거래 준비 중...');

    const startTime = Date.now();

    try {
      let txHash;

      if (tokenToTransfer.isNative) {
        console.log(`💰 WLC 전송: ${transferAmount} WLC → ${recipientAddress}`);
        setTransferProgress('WLC 전송 거래 생성 중...');
        txHash = await sendWLC();
      } else {
        console.log(`🪙 토큰 전송: ${transferAmount} ${tokenToTransfer.symbol} → ${recipientAddress}`);
        setTransferProgress('토큰 전송 거래 생성 중...');
        txHash = await sendToken();
      }

      setTransferStatus('pending');
      setTransferProgress('거래가 블록체인에 전송되었습니다...');
      setSuccessTx(txHash);

      // 거래 확인 대기
      setTransferStatus('confirming');
      const { success, receipt, gasUsed } = await waitForTransaction(txHash);

      const endTime = Date.now();
      const duration = Math.round((endTime - startTime) / 1000);

      if (success) {
        setTransferStatus('completed');
        setTransferProgress('전송 완료!');

        // 전송 결과 요약 설정
        setTransferResult({
          success: true,
          txHash: txHash,
          token: tokenToTransfer,
          amount: transferAmount,
          recipient: recipientAddress,
          gasUsed: gasUsed,
          gasEstimate: gasEstimate,
          duration: duration,
          blockNumber: receipt ? parseInt(receipt.blockNumber, 16) : null,
          timestamp: Date.now()
        });

        console.log(`✅ 전송 성공: ${txHash}`);

        // 잔액 즉시 업데이트
        if (selectedToken) {
          setTransferProgress('잔액 업데이트 중...');
          await updateTokenBalance(selectedToken);
        }

        // 성공 후 폼 초기화 (일정 시간 후)
        setTimeout(() => {
          setRecipientAddress('');
          setTransferAmount('');
          setGasEstimate(null);
          setTransferStatus('idle');
          setTransferProgress('');
        }, 3000);

      } else {
        throw new Error('거래가 실패했습니다. 거래 영수증을 확인해주세요.');
      }

    } catch (error) {
      console.error('❌ 전송 실패:', error);

      setTransferStatus('failed');
      
      let errorMessage = '전송 중 오류가 발생했습니다.';

      if (error.code === 4001) {
        errorMessage = '사용자가 거래를 취소했습니다.';
        setTransferProgress('거래가 취소되었습니다.');
      } else if (error.code === -32603) {
        errorMessage = '거래 실행 중 내부 오류가 발생했습니다.';
        setTransferProgress('거래 실행 실패');
      } else if (error.code === -32000) {
        errorMessage = '잔액 부족 또는 가스 부족입니다.';
        setTransferProgress('잔액 또는 가스 부족');
      } else if (error.message) {
        errorMessage = error.message;
        setTransferProgress('거래 실패');
      }

      // 실패 결과 요약 설정
      setTransferResult({
        success: false,
        error: errorMessage,
        token: tokenToTransfer,
        amount: transferAmount,
        recipient: recipientAddress,
        gasEstimate: gasEstimate,
        timestamp: Date.now()
      });

      setErrors([errorMessage]);
      
      // 실패 후 상태 초기화 (일정 시간 후)
      setTimeout(() => {
        setTransferStatus('idle');
        setTransferProgress('');
      }, 5000);

    } finally {
      setIsProcessing(false);
    }
  };

  // 주소 복사
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('주소가 클립보드에 복사되었습니다.');
    });
  };

  // 최대 수량 설정
  const setMaxAmount = () => {
    if (selectedToken) {
      setTransferAmount(selectedToken.balance);
      estimateGas();
    }
  };

  // 가스 예상 (디바운스)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (recipientAddress && transferAmount && (selectedToken || customTokenAddress)) {
        estimateGas();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [recipientAddress, transferAmount, selectedToken, customTokenAddress]);

  return (
    <div className="token-transfer">
      {/* Header */}
      <div className="transfer-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft className="icon" />
          뒤로가기
        </button>
        <h1>Token Transfer</h1>
      </div>

      {/* Token Selection Section */}
      <div className="token-selection-section">
        <div className="section-header">
          <h3>전송할 토큰 선택</h3>
          <button 
            onClick={fetchUserTokens}
            disabled={isLoadingTokens}
            className="refresh-btn"
            title="토큰 목록 새로고침"
          >
            <RefreshCw className={`icon ${isLoadingTokens ? 'spinning' : ''}`} />
            {isLoadingTokens ? '로딩 중...' : '새로고침'}
          </button>
        </div>
        
        {isLoadingTokens ? (
          <LoadingSpinner />
        ) : (
          <div className="token-list">
            {userTokens.length === 0 ? (
              <div className="no-tokens">
                <Coins className="icon" />
                <p>보유한 토큰이 없습니다.</p>
              </div>
            ) : (
              userTokens.map((token, index) => (
                <div 
                  key={index}
                  className={`token-item ${selectedToken?.address === token.address ? 'selected' : ''}`}
                  onClick={() => selectToken(token)}
                >
                  <div className="token-info">
                    <div className="token-name">
                      <span className="token-symbol">{token.symbol}</span>
                      <span className="token-full-name">{token.name}</span>
                      {token.verified && <span className="verified-badge">✓</span>}
                    </div>
                    <div className="token-balance">
                      <span className="balance-amount">{token.balance}</span>
                      <span className="balance-symbol">{token.symbol}</span>
                    </div>
                  </div>
                  {!token.isNative && (
                    <div className="token-address">
                      <span className="address-value" title={token.address}>
                        {token.address.substring(0, 6)}...{token.address.substring(token.address.length - 4)}
                      </span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(token.address);
                        }}
                        className="copy-btn"
                        title="주소 복사"
                      >
                        <Copy className="icon" />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
        
        {/* 커스텀 토큰 주소 입력 */}
        <div className="custom-token-section">
          <h4>또는 토큰 주소 직접 입력</h4>
          <div className="custom-token-input">
            <input
              type="text"
              placeholder="토큰 컨트랙트 주소 (0x...)"
              value={customTokenAddress}
              onChange={(e) => handleCustomTokenAddress(e.target.value)}
              className="address-input"
            />
          </div>
        </div>
      </div>

      {/* Transfer Form */}
      <div className="transfer-form-section">
        <h3>전송 정보 입력</h3>
        
        <div className="form-group">
          <label>수신자 주소</label>
          <div className="address-input-group">
            <input
              type="text"
              placeholder="수신자 지갑 주소 (0x...)"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              className="address-input"
              disabled={isProcessing}
            />
          </div>
        </div>

        <div className="form-group">
          <label>전송 수량</label>
          <div className="amount-input-group">
            <input
              type="number"
              placeholder="전송할 수량"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              className="amount-input"
              step="0.000001"
              min="0"
              disabled={isProcessing}
            />
            {selectedToken && (
              <div className="amount-actions">
                <button 
                  onClick={setMaxAmount}
                  className="max-btn"
                  disabled={isProcessing}
                >
                  MAX
                </button>
                <span className="token-symbol">{selectedToken.symbol}</span>
              </div>
            )}
          </div>
          {selectedToken && (
            <div className="balance-info">
              <span>보유 잔액: {selectedToken.balance} {selectedToken.symbol}</span>
            </div>
          )}
        </div>

        {/* Gas Estimate */}
        {gasEstimate && (
          <div className="gas-estimate">
            <div className="gas-info">
              <span className="gas-label">예상 가스비:</span>
              <span className="gas-amount">{gasEstimate} WLC</span>
            </div>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="errors-section">
          <div className="error-header">
            <AlertCircle className="icon" />
            <span>오류</span>
          </div>
          <ul className="error-list">
            {errors.map((error, index) => (
              <li key={index} className="error-item">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Transfer Progress */}
      {isProcessing && (
        <div className="progress-section">
          <div className="progress-header">
            <div className="progress-title">
              <div className="progress-spinner"></div>
              <span>전송 진행 중</span>
            </div>
            <div className="progress-status">
              {transferStatus === 'preparing' && '준비 중...'}
              {transferStatus === 'pending' && '전송 중...'}
              {transferStatus === 'confirming' && '확인 중...'}
              {transferStatus === 'completed' && '완료!'}
              {transferStatus === 'failed' && '실패'}
            </div>
          </div>
          
          <div className="progress-steps">
            <div className={`step ${transferStatus === 'preparing' || transferStatus === 'pending' || transferStatus === 'confirming' || transferStatus === 'completed' ? 'active' : ''} ${transferStatus === 'completed' ? 'completed' : ''}`}>
              <div className="step-circle">1</div>
              <span>거래 생성</span>
            </div>
            <div className={`step ${transferStatus === 'pending' || transferStatus === 'confirming' || transferStatus === 'completed' ? 'active' : ''} ${transferStatus === 'completed' ? 'completed' : ''}`}>
              <div className="step-circle">2</div>
              <span>블록체인 전송</span>
            </div>
            <div className={`step ${transferStatus === 'confirming' || transferStatus === 'completed' ? 'active' : ''} ${transferStatus === 'completed' ? 'completed' : ''}`}>
              <div className="step-circle">3</div>
              <span>거래 확인</span>
            </div>
            <div className={`step ${transferStatus === 'completed' ? 'active completed' : ''}`}>
              <div className="step-circle">4</div>
              <span>완료</span>
            </div>
          </div>
          
          <div className="progress-message">
            <span>{transferProgress}</span>
          </div>

          {successTx && (
            <div className="tx-info-progress">
              <div className="tx-hash-display">
                <span className="tx-label">거래 해시:</span>
                <div className="tx-hash-group">
                  <span className="tx-hash">{successTx.substring(0, 10)}...{successTx.substring(successTx.length - 8)}</span>
                  <button 
                    onClick={() => copyToClipboard(successTx)}
                    className="copy-btn"
                    title="TX Hash 복사"
                  >
                    <Copy className="icon" />
                  </button>
                  <a 
                    href={`https://scan.worldland.foundation/tx/${successTx}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="explorer-btn"
                    title="Explorer에서 보기"
                  >
                    <ExternalLink className="icon" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transfer Result Summary */}
      {transferResult && !isProcessing && (
        <div className={`result-section ${transferResult.success ? 'success' : 'failed'}`}>
          <div className="result-header">
            {transferResult.success ? (
              <>
                <CheckCircle className="icon" />
                <span>전송 완료</span>
              </>
            ) : (
              <>
                <AlertCircle className="icon" />
                <span>전송 실패</span>
              </>
            )}
          </div>

          <div className="result-content">
            <div className="result-summary">
              <h4>전송 요약</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="label">토큰:</span>
                  <span className="value">{transferResult.token.symbol}</span>
                </div>
                <div className="summary-item">
                  <span className="label">수량:</span>
                  <span className="value">{transferResult.amount} {transferResult.token.symbol}</span>
                </div>
                <div className="summary-item">
                  <span className="label">수신자:</span>
                  <span className="value">{transferResult.recipient.substring(0, 10)}...{transferResult.recipient.substring(transferResult.recipient.length - 8)}</span>
                </div>
                {transferResult.success && (
                  <>
                    <div className="summary-item">
                      <span className="label">실제 가스비:</span>
                      <span className="value">{actualGasUsed || 'N/A'} WLC</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">예상 가스비:</span>
                      <span className="value">{transferResult.gasEstimate || 'N/A'} WLC</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">소요 시간:</span>
                      <span className="value">{transferResult.duration || 'N/A'}초</span>
                    </div>
                    {transferResult.blockNumber && (
                      <div className="summary-item">
                        <span className="label">블록 번호:</span>
                        <span className="value">#{transferResult.blockNumber}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="summary-item">
                  <span className="label">시간:</span>
                  <span className="value">{new Date(transferResult.timestamp).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {transferResult.success && transferResult.txHash && (
              <div className="tx-actions">
                <button 
                  onClick={() => copyToClipboard(transferResult.txHash)}
                  className="action-btn copy"
                >
                  <Copy className="icon" />
                  TX Hash 복사
                </button>
                <a 
                  href={`https://scan.worldland.foundation/tx/${transferResult.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn explorer"
                >
                  <ExternalLink className="icon" />
                  Explorer에서 보기
                </a>
              </div>
            )}

            {!transferResult.success && (
              <div className="error-details">
                <h5>오류 상세:</h5>
                <p>{transferResult.error}</p>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              setTransferResult(null);
              setSuccessTx(null);
              setActualGasUsed(null);
            }}
            className="close-result-btn"
          >
            결과 닫기
          </button>
        </div>
      )}

      {/* Transfer Summary & Execute Button */}
      <div className="execute-section">
        {(selectedToken || customTokenAddress) && recipientAddress && transferAmount && (
          <div className="transfer-summary">
            <h4>전송 요약</h4>
            <div className="summary-item">
              <span className="label">토큰:</span>
              <span className="value">{selectedToken ? `${selectedToken.name} (${selectedToken.symbol})` : '커스텀 토큰'}</span>
            </div>
            <div className="summary-item">
              <span className="label">수량:</span>
              <span className="value">{transferAmount} {selectedToken?.symbol || 'TOKEN'}</span>
            </div>
            <div className="summary-item">
              <span className="label">수신자:</span>
              <span className="value">{recipientAddress.substring(0, 10)}...{recipientAddress.substring(recipientAddress.length - 8)}</span>
            </div>
            {gasEstimate && (
              <div className="summary-item">
                <span className="label">예상 가스비:</span>
                <span className="value">{gasEstimate} WLC</span>
              </div>
            )}
          </div>
        )}

        <button 
          onClick={executeTransfer}
          disabled={isProcessing || errors.length > 0 || !recipientAddress || !transferAmount || (!selectedToken && !customTokenAddress)}
          className="execute-btn"
        >
          {isProcessing ? (
            <>
              <div className="spinner"></div>
              전송 중...
            </>
          ) : (
            <>
              <Send className="icon" />
              토큰 전송
            </>
          )}
        </button>

        <div className="transfer-warning">
          <AlertCircle className="icon" />
          <p>전송은 되돌릴 수 없습니다. 수신자 주소를 정확히 확인해주세요.</p>
        </div>
      </div>
    </div>
  );
};

export default TokenTransfer;